import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState
} from 'react';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {api, baseUrl, handleResponse, jsonInit} from '../api/client';
import {decodeJwt} from '../lib/jwt';
import {AuthError, AuthTokens, Me, ProblemDetails} from '../types';

// Tokens live in localStorage: on a phone the installed app is closed and reopened all day and
// must stay signed in. The access token lasts an hour; the refresh token rotates for 30 days.

const TEN_SECONDS = 10 * 1000;

type StorageKey = 'access_token' | 'refresh_token';

const getItem = (key: StorageKey): string => {
    try {
        return window.localStorage.getItem(key) ?? '';
    } catch {
        return '';
    }
};

const setItem = (key: StorageKey, value: string) => {
    try {
        if (value) window.localStorage.setItem(key, value);
        else window.localStorage.removeItem(key);
    } catch {
        // private mode: the session will not persist, that is all
    }
};

export interface Claims {
    id: string;
    email: string;
    givenName: string;
    isAdmin: boolean;
}

const claimsOf = (accessToken: string): Claims | null => {
    if (!accessToken) return null;
    try {
        const {payload} = decodeJwt(accessToken);
        return {
            id: String(payload.sub),
            email: String(payload.email ?? ''),
            givenName: String(payload.given_name ?? ''),
            isAdmin: payload.admin === true
        };
    } catch {
        return null;
    }
};

const expiringSoon = (accessToken: string): boolean => {
    try {
        const {payload} = decodeJwt(accessToken);
        return Date.now() >= Number(payload.exp) * 1000 - TEN_SECONDS;
    } catch {
        return true;
    }
};

export type Auth = {
    isAuthenticated: boolean;
    claims: Claims | null;
    getAccessToken(): Promise<string>;
    onAuthenticated(tokens: AuthTokens): void;
    logout(): Promise<void>;
    /** fetch with the bearer header, refreshing first when the token is about to expire. */
    authedFetch(path: string, init?: RequestInit): Promise<Response>;
    /** Typed JSON call through authedFetch. */
    call<T>(path: string, init?: RequestInit): Promise<T>;
};

const AuthContext = createContext<Auth>({
    isAuthenticated: false,
    claims: null,
    getAccessToken: async () => '',
    onAuthenticated: () => {},
    logout: async () => {},
    authedFetch: () => fetch(''),
    call: () => Promise.reject(new Error('no auth provider'))
});

function useAuthImpl(): Auth {
    const [accessToken, setAccessToken] = useState(() => getItem('access_token'));
    const [refreshToken, setRefreshToken] = useState(() => getItem('refresh_token'));
    const refreshing = useRef<Promise<string> | null>(null);
    const queryClient = useQueryClient();

    const claims = useMemo(() => claimsOf(accessToken), [accessToken]);

    const clear = useCallback(() => {
        setAccessToken('');
        setRefreshToken('');
        setItem('access_token', '');
        setItem('refresh_token', '');
        queryClient.clear();
    }, [queryClient]);

    const onAuthenticated = useCallback((tokens: AuthTokens) => {
        setAccessToken(tokens.access_token);
        setRefreshToken(tokens.refresh_token);
        setItem('access_token', tokens.access_token);
        setItem('refresh_token', tokens.refresh_token);
    }, []);

    const refresh = useCallback(async (): Promise<string> => {
        const response = await fetch(`${baseUrl}/api/auth/refresh-authentication`, jsonInit('POST', {token: refreshToken}));
        const body = (await response.json()) as AuthTokens | AuthError | ProblemDetails;
        if (!('access_token' in body) || !body.access_token) {
            clear();
            return '';
        }
        onAuthenticated(body);
        return body.access_token;
    }, [refreshToken, clear, onAuthenticated]);

    const getAccessToken = useCallback(async (): Promise<string> => {
        if (!accessToken) return '';
        if (!expiringSoon(accessToken)) return accessToken;
        if (!refreshing.current) {
            refreshing.current = refresh().finally(() => {
                refreshing.current = null;
            });
        }
        try {
            return await refreshing.current;
        } catch {
            clear();
            return '';
        }
    }, [accessToken, refresh, clear]);

    const authedFetch = useCallback(
        async (path: string, init: RequestInit = {}): Promise<Response> => {
            const headers = new Headers(init.headers);
            const token = await getAccessToken();
            if (token) headers.set('Authorization', `Bearer ${token}`);
            const response = await fetch(`${baseUrl}${path}`, {...init, headers, credentials: 'include'});
            if (response.status === 401) clear();
            return response;
        },
        [getAccessToken, clear]
    );

    const call = useCallback(
        async <T,>(path: string, init?: RequestInit): Promise<T> => handleResponse<T>(await authedFetch(path, init)),
        [authedFetch]
    );

    const logout = useCallback(async () => {
        const token = refreshToken;
        clear();
        if (token) {
            await api('/api/auth/logout', jsonInit('POST', {token})).catch(() => undefined);
        }
    }, [refreshToken, clear]);

    return {
        isAuthenticated: claims !== null,
        claims,
        getAccessToken,
        onAuthenticated,
        logout,
        authedFetch,
        call
    };
}

export const AuthProvider = ({children}: {children: ReactNode}) => {
    const auth = useAuthImpl();
    return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => useContext(AuthContext);

/** The signed-in user's profile from /api/auth/me (cached; invalidate after editing it). */
// eslint-disable-next-line react-refresh/only-export-components
export const useMe = () => {
    const {isAuthenticated, call} = useAuth();
    return useQuery({
        queryKey: ['me'],
        queryFn: () => call<Me>('/api/auth/me'),
        enabled: isAuthenticated,
        staleTime: 5 * 60 * 1000
    });
};
